import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, LineChart, Line, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Database, CheckCircle2, DollarSign, TrendingUp, AlertCircle, Loader2,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { formatNumber } from './format';

function StatTile({ icon: Icon, value, label, sub, variant, loading }) {
  return (
    <div className={`bb-stat ${variant ? `bb-stat--${variant}` : ''}`}>
      <div className="bb-stat__icon"><Icon size={16} /></div>
      <div className="bb-stat__body">
        <div className="bb-stat__value">
          {loading ? <Loader2 size={18} className="bb-spin" /> : value}
        </div>
        <div className="bb-stat__label">{label}</div>
        {sub != null && <div className="bb-stat__sub">{sub}</div>}
      </div>
    </div>
  );
}

function Sparkline({ data }) {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className="bb-spark bb-spark--empty">No trend data yet</div>;
  }
  return (
    <div className="bb-spark">
      <ResponsiveContainer width="100%" height={56}>
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <XAxis dataKey="date" hide />
          <YAxis hide />
          <Tooltip
            formatter={(v) => formatNumber(v)}
            labelFormatter={(l) => l}
            contentStyle={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              fontSize: 12,
            }}
          />
          <Line
            type="monotone"
            dataKey="classified"
            stroke="var(--color-primary)"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function MethodEffectiveness({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return <div className="bb-method bb-method--empty">No method data yet.</div>;
  }
  const total = rows.reduce((s, r) => s + (Number(r.count) || 0), 0) || 1;
  return (
    <div className="bb-method">
      <h4 className="bb-section__h4">Classification rate by method</h4>
      <ul className="bb-method__list">
        {rows.map((r) => {
          const pct = ((Number(r.count) || 0) / total) * 100;
          return (
            <li key={r.method} className="bb-method__row">
              <span className="bb-method__name">{r.method}</span>
              <span className="bb-method__bar">
                <span
                  className="bb-method__fill"
                  style={{ width: `${Math.min(100, pct).toFixed(1)}%` }}
                />
              </span>
              <span className="bb-method__count">{formatNumber(r.count)}</span>
              {r.avg_cost_usd != null && (
                <span className="bb-method__cost">${Number(r.avg_cost_usd).toFixed(4)}/item</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ProgressDashboard() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['backlog-buster', 'progress'],
    queryFn: () => apiClient.get('/api/backlog-buster/progress'),
    staleTime: 60_000,
  });

  if (error) {
    return (
      <section className="bb-section bb-section--error">
        <header className="bb-section__head">
          <div className="bb-section__title"><TrendingUp size={16} /> Progress</div>
        </header>
        <div className="bb-state bb-state--error">
          <AlertCircle size={16} /> Failed to load progress: {error.message}
          <button className="bb-btn bb-btn--ghost" onClick={() => refetch()}>Retry</button>
        </div>
      </section>
    );
  }

  const backlog       = data?.backlog_size ?? data?.backlogRemaining ?? null;
  const thisWeek      = data?.classified_this_week ?? data?.classifiedThisWeek ?? null;
  const thisMonth     = data?.classified_this_month ?? data?.classifiedThisMonth ?? null;
  const costPer       = data?.cost_per_classification ?? data?.costPerClassification ?? null;
  const byMethod      = data?.by_method ?? data?.method_effectiveness ?? [];
  const trend         = data?.trend_30_day ?? data?.trend30Day ?? [];
  const byTier        = data?.by_tier ?? data?.classification_rate_by_tier ?? [];

  const loading = isLoading;

  return (
    <section className="bb-section">
      <header className="bb-section__head">
        <div className="bb-section__title"><TrendingUp size={16} /> Progress</div>
        {isFetching && !isLoading && (
          <span className="bb-section__hint"><Loader2 size={12} className="bb-spin" /> Refreshing</span>
        )}
      </header>

      <div className="bb-stats">
        <StatTile
          icon={Database}
          value={backlog != null ? formatNumber(backlog) : '—'}
          label="Backlog remaining"
          variant="primary"
          loading={loading}
        />
        <StatTile
          icon={CheckCircle2}
          value={thisWeek != null ? formatNumber(thisWeek) : '—'}
          sub={thisMonth != null ? `${formatNumber(thisMonth)} last 30 days` : null}
          label="Classified this week"
          variant="success"
          loading={loading}
        />
        <StatTile
          icon={DollarSign}
          value={costPer != null ? `$${Number(costPer).toFixed(4)}` : '—'}
          label="Avg cost / classification"
          loading={loading}
        />
        <div className="bb-stat bb-stat--spark">
          <div className="bb-stat__icon"><TrendingUp size={16} /></div>
          <div className="bb-stat__body">
            <div className="bb-stat__label">30-day trend</div>
            <Sparkline data={trend} />
          </div>
        </div>
      </div>

      {byTier?.length > 0 && (
        <div className="bb-tiers">
          {byTier.map((t) => (
            <div key={t.tier} className="bb-tier">
              <span className="bb-tier__name">{t.tier}</span>
              <span className="bb-tier__count">{formatNumber(t.classified ?? t.count)}</span>
              <span className="bb-tier__total">
                of {formatNumber(t.total ?? t.attempted ?? '—')}
              </span>
              {t.success_rate != null && (
                <span className="bb-tier__pct">{Number(t.success_rate).toFixed(1)}%</span>
              )}
            </div>
          ))}
        </div>
      )}

      <MethodEffectiveness rows={byMethod} />
    </section>
  );
}

export default ProgressDashboard;
